/* @jsx h */
import { pp } from "@app/utils/clientBusiness";
import { cn } from "@lemmonhq/uikit";
import { FloorPlanTable } from "@lemmonhq/types";
import { nanoid } from "nanoid";
import { defineComponent, onMounted, onUnmounted, ref, Teleport, h } from "vue";
import { FloorPlanOverylay } from "./FloorPlanOverylay";
import { FloorPlanItemForm } from "./FloorPlanItemForm";
import { TableSheet } from "./TableSheet/index";
import { FloorPlanSelection, TablesFloorplanProps } from "./types";
import { FloorPlanList } from "./FloorPlanList";
import { FloorPlanWalker } from "./FloorPlanWalker";
import { useAuth } from "@app/vue/providers/Auth";

function isTouchEvent(ev: PointerEvent | TouchEvent): ev is TouchEvent {
  return "touches" in ev;
}

export const createCanvasItem = (
  selection: FloorPlanSelection,
  existingTableDef?: FloorPlanTable,
) => {
  const area = selection.area;
  if (!area) throw new Error("area not provided");
  console.log("createCanvasItem", pp(area));
  const tableDef: FloorPlanTable = existingTableDef
    ? {
        ...existingTableDef,
        colStart: area.start.col,
        colEnd: area.end.col + 0,
        rowStart: area.start.row,
        rowEnd: area.end.row + 0,
      }
    : {
        id: nanoid(),
        colStart: area.start.col,
        colEnd: area.end.col + 0,
        rowStart: area.start.row,
        rowEnd: area.end.row + 0,
      };
  // Normalize: ensure start <= end
  if (tableDef.colEnd < tableDef.colStart) {
    [tableDef.colStart, tableDef.colEnd] = [tableDef.colEnd, tableDef.colStart];
  }
  if (tableDef.rowEnd < tableDef.rowStart) {
    [tableDef.rowStart, tableDef.rowEnd] = [tableDef.rowEnd, tableDef.rowStart];
  }
  // Tables can span any number of cells in both dimensions — no constraints
  console.log("tableDef", pp(tableDef));
  return tableDef;
};

export const TablesFloorplan = defineComponent<TablesFloorplanProps>({
  name: "FloorPlanEditor",
  props: [
    "tables",
    "floorPlan",
    "onFloorPlanChange",
    "onPayment",
    "isEditable",
    "isLoading",
    "dropModeState",
    "onTableClickInDropMode",
    "reservations",
    "reservationsByTable",
    "isLiveDay",
    "nowMs",
  ],
  setup(props) {
    const { posMode, user } = useAuth();
    const gridRef = ref<HTMLElement | null>(null);
    const selection = ref<FloorPlanSelection>({});
    const selectedTable = ref<FloorPlanTable | undefined>();
    const activeTable = ref<FloorPlanTable | undefined>();
    const isTableSelectModalOpen = ref<FloorPlanTable>();

    // Determine if walker should show after DOM is committed (not during render)
    const showWalker = ref(false);
    onMounted(() => {
      // Only show walker if this is the sole floor plan grid on the page
      showWalker.value =
        document.querySelectorAll(".floor-plan-grid").length <= 1;
    });

    const renderGrid = () => {
      const GRID_ROWS = props.floorPlan.rows;
      const GRID_COLS = props.floorPlan.columns;

      const cells = [];
      for (let row = 0; row < GRID_ROWS; row++) {
        for (let col = 0; col < GRID_COLS; col++) {
          cells.push(
            <div
              key={`${row}-${col}`}
              class={cn(
                "aspect-square relative floor-plan-cell",
                props.isEditable ? "cursor-crosshair" : "cursor-default",
              )}
              style={
                props.isEditable
                  ? "border: 0.5px solid var(--floor-plan-grid-line);"
                  : ""
              }
              data-row={row}
              data-col={col}
            ></div>,
          );
        }
      }
      return cells;
    };
    const pointerStart = ref<{ x: number; y: number } | null>(null);
    const pointerMoved = ref(false);
    // pixels

    const handlePointerDown = (ev: PointerEvent | TouchEvent) => {
      const el = ev.target as HTMLElement;
      // Only handle events within this grid
      if (
        gridRef.value &&
        !gridRef.value.contains(el) &&
        !el.closest("[data-plan-item]")
      )
        return;
      const planItem = el.closest("[data-plan-item]") as HTMLElement;
      const row = el.dataset?.row;
      const col = el.dataset?.col;
      const point = isTouchEvent(ev)
        ? { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
        : { x: ev.clientX, y: ev.clientY };

      pointerStart.value = point;
      pointerMoved.value = false;

      // Handle clicking on existing table (data-plan-item)
      if (planItem) {
        const planItemId = planItem.dataset.planItem;
        const planItemRow = Number(planItem.dataset.row);
        const planItemCol = Number(planItem.dataset.col);

        selectedTable.value = props.floorPlan.plan.find(
          (t) => t.id === planItemId,
        );

        if (selectedTable.value) {
          selection.value = {
            tableId: selectedTable.value.tableId,
            area: {
              start: { row: planItemRow, col: planItemCol },
              end: {
                row: selectedTable.value.rowEnd,
                col: selectedTable.value.colEnd,
              },
            },
          };
        }
      }
      // Handle clicking on empty grid cell (manager mode only)
      else if (row && col && props.isEditable) {
        const gridPoint = { row: Number(row), col: Number(col) };

        selection.value = {
          area: {
            start: gridPoint,
            end: gridPoint,
          },
        };

        selectedTable.value = undefined;
      }
    };

    const handlePointerMove = (ev: PointerEvent | TouchEvent) => {
      if (!pointerStart.value) return;
      const point = isTouchEvent(ev)
        ? { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
        : { x: ev.clientX, y: ev.clientY };

      const dx = point.x - pointerStart.value.x;
      const dy = point.y - pointerStart.value.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 10) {
        // 10px threshold as requested
        pointerMoved.value = true;

        // Update pan for visual feedback during drag
        selection.value.pan = {
          start: pointerStart.value,
          end: point,
        };

        // Get the cell under the pointer for overlay highlighting
        const el = document.elementFromPoint(point.x, point.y) as HTMLElement;
        const gridCell = el?.closest(".floor-plan-cell") as HTMLElement;
        const row = gridCell?.dataset?.row;
        const col = gridCell?.dataset?.col;

        if (row && col && selection.value.area?.start) {
          // If dragging a table, update selection area to show where it will be placed
          if (selectedTable.value) {
            const newRow = Number(row);
            const newCol = Number(col);
            const tableWidth =
              selectedTable.value.colEnd - selectedTable.value.colStart;
            const tableHeight =
              selectedTable.value.rowEnd - selectedTable.value.rowStart;

            selection.value.area = {
              start: { row: newRow, col: newCol },
              end: { row: newRow + tableHeight, col: newCol + tableWidth },
            };
          } else if (props.isEditable) {
            // Creating new table area (manager mode only)
            selection.value.area.end = { row: Number(row), col: Number(col) };
          }
        }
      }
    };

    const handlePointerUp = (ev: PointerEvent | TouchEvent) => {
      if (!pointerStart.value) return;

      //ev.preventDefault();
      //ev.stopPropagation();

      const point = isTouchEvent(ev)
        ? { x: ev.changedTouches[0].clientX, y: ev.changedTouches[0].clientY }
        : { x: ev.clientX, y: ev.clientY };

      const dx = point.x - pointerStart.value.x;
      const dy = point.y - pointerStart.value.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Treat as a click if movement is small. In drop mode the floor is
      // tap-to-select (dragging tables is disabled), so be far more forgiving
      // of sloppy clicks — otherwise a slight drag is wrongly ignored and the
      // user has to click several times.
      const clickThreshold = props.dropModeState?.value.active ? 22 : 10;
      if (distance < clickThreshold && selectedTable.value) {
        // Check if we're in drop mode
        if (props.dropModeState?.value.active) {
          // In drop mode, clicking a table should trigger the seating flow
          if (selectedTable.value.tableId) {
            props.onTableClickInDropMode?.(selectedTable.value.tableId);
          }
        } else if (!selectedTable.value.tableId) {
          if (user.value?.role == "manager" && !posMode.value) {
            isTableSelectModalOpen.value = selectedTable.value;
          }
        } else {
          console.log(
            "setting active table",
            pp({
              selectedTable: selectedTable.value,
              activeTable: activeTable.value,
            }),
          );
          activeTable.value = selectedTable.value;
        }
      }
      // If dragging a table, update its position
      else if (pointerMoved.value && selectedTable.value) {
        const el = document.elementFromPoint(point.x, point.y) as HTMLElement;
        const row = el?.dataset?.row;
        const col = el?.dataset?.col;

        // Don't allow editing in drop mode
        if (
          row &&
          col &&
          props.isEditable &&
          !props.dropModeState?.value.active
        ) {
          const newRow = Number(row);
          const newCol = Number(col);
          const tableWidth =
            selectedTable.value.colEnd - selectedTable.value.colStart;
          const tableHeight =
            selectedTable.value.rowEnd - selectedTable.value.rowStart;

          const updatedTable: FloorPlanTable = {
            ...selectedTable.value,
            rowStart: newRow,
            rowEnd: newRow + tableHeight,
            colStart: newCol,
            colEnd: newCol + tableWidth,
          };

          props.onFloorPlanChange({
            ...props.floorPlan,
            plan: props.floorPlan.plan.map((t) =>
              t.id === selectedTable.value?.id ? updatedTable : t,
            ),
          });
        }
      }
      // If creating new table area
      else if (
        selection.value.area &&
        !selectedTable.value &&
        props.isEditable &&
        !props.dropModeState?.value.active
      ) {
        const tableDef = createCanvasItem(selection.value);
        props.onFloorPlanChange({
          ...props.floorPlan,
          plan: [...props.floorPlan.plan, tableDef],
        });
        // Open the form immediately so the user can configure the new item
        isTableSelectModalOpen.value = tableDef;
      }

      pointerStart.value = null;
      pointerMoved.value = false;
      selection.value = {};
      selectedTable.value = undefined;
    };

    onMounted(() => {
      console.log("mounted");
      if (window.matchMedia("(pointer: coarse)").matches) {
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("touchmove", handlePointerMove);
        document.addEventListener("touchend", handlePointerUp);
      } else {
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp);
      }

      onUnmounted(() => {
        if (window.matchMedia("(pointer: coarse)").matches) {
          document.removeEventListener("touchstart", handlePointerDown);
          document.removeEventListener("touchmove", handlePointerMove);
          document.removeEventListener("touchend", handlePointerUp);
        } else {
          document.removeEventListener("pointerdown", handlePointerDown);
          document.removeEventListener("pointermove", handlePointerMove);
          document.removeEventListener("pointerup", handlePointerUp);
        }
      });
    });
    const renderGridPadding = () => {
      const GRID_ROWS = props.floorPlan.rows;

      if (!props.isEditable) return <div class="flex-1" style={``} />;
      return (
        <div
          style={`
                  grid-template-rows: repeat(${GRID_ROWS},minmax(0,1fr));
                  `}
          class="flex-1 grid bg-[var(--floor-plan-grid-padding)]"
        >
          {new Array(GRID_ROWS).fill(true).map((el, i) => {
            return <div class="border-b " key={i} />;
          })}
        </div>
      );
    };

    return () => {
      const GRID_ROWS = props.floorPlan.rows;
      const GRID_COLS = props.floorPlan.columns;
      const isDropMode = props.dropModeState?.value.active;
      console.log("re rendering;");
      return (
        <div
          class="p-0 flex-1 flex flex-col"
          style={
            props.isEditable ? "touch-action: none; overflow: hidden;" : ""
          }
        >
          <style>{`
            /* Pinch-zoom only on the grid, not the whole POS */
            .floor-plan-grid {
              touch-action: pinch-zoom pan-x pan-y;
            }
            /* Staggered entrance */
            .floor-plan-enter {
              animation: floorPlanFadeIn 0.15s ease both;
            }
            @keyframes floorPlanFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
          <div class="mx-auto flex-1 flex flex-col w-full h-full">
            <div class="p-0 rounded-lg relative flex-1 flex flex-col">
              <div style="container-type: size;" class="flex-1 flex flex-row">
                {renderGridPadding()}
                <div
                  ref={gridRef}
                  style={`
                    grid-template-columns: repeat(${GRID_COLS}, 1fr);
                    grid-template-rows: repeat(${GRID_ROWS},minmax(0,1fr));
                    width: min(100cqw, calc(100cqh * ${GRID_COLS}/${GRID_ROWS}));
                    height: min(100cqh, calc(100cqw * ${GRID_ROWS}/${GRID_COLS}));
                    background: ${props.floorPlan.color || "var(--floor-plan-background)"};
                  `}
                  class="relative grid p-0 flex-1 rounded-lg floor-plan-grid"
                >
                  {renderGrid()}
                  <div
                    class="absolute inset-0 pointer-events-none"
                    style="z-index: 5;"
                  >
                    <FloorPlanList
                      tables={props.tables}
                      floorPlan={props.floorPlan}
                      onFloorPlanChange={props.onFloorPlanChange}
                      onPayment={props.onPayment}
                      isEditable={props.isEditable && !isDropMode}
                      isLoading={props.isLoading}
                      dropModeState={props.dropModeState}
                      reservations={props.reservations}
                      reservationsByTable={props.reservationsByTable}
                      isLiveDay={props.isLiveDay}
                      nowMs={props.nowMs}
                      selection={selection}
                      gridRef={gridRef}
                      selectedTable={selectedTable}
                      activeTable={activeTable}
                      onTableOpen={(tableId) => {}}
                    />
                  </div>
                  {!props.isEditable && showWalker.value && (
                    <FloorPlanWalker
                      gridRef={gridRef}
                      floorPlan={props.floorPlan}
                    />
                  )}
                </div>
                {renderGridPadding()}
              </div>
              <Teleport to="body">
                {props.isEditable ? (
                  <FloorPlanOverylay
                    tables={props.tables}
                    floorPlan={props.floorPlan}
                    onFloorPlanChange={props.onFloorPlanChange}
                    onPayment={props.onPayment}
                    isEditable={props.isEditable}
                    isLoading={props.isLoading}
                    nowMs={props.nowMs}
                    selection={selection}
                    gridRef={gridRef}
                  />
                ) : null}
              </Teleport>
            </div>
          </div>
          <Teleport to="body">
            {isTableSelectModalOpen.value && !isDropMode ? (
              <div style="z-index: 9999;">
                <FloorPlanItemForm
                  planConfig={isTableSelectModalOpen.value}
                  floorPlan={props.floorPlan}
                  tables={props.tables}
                  onClose={() => (isTableSelectModalOpen.value = undefined)}
                  onUpdate={(fp) => {
                    props.onFloorPlanChange(fp);
                  }}
                  onSelect={(tableDef) => {
                    console.log("tableDef", tableDef);
                    props.onFloorPlanChange({
                      ...props.floorPlan,
                      plan: [
                        ...props.floorPlan.plan.filter(
                          (el) => el.id !== tableDef?.id,
                        ),
                        tableDef,
                      ],
                    });
                    isTableSelectModalOpen.value = undefined;
                  }}
                />
              </div>
            ) : null}
            {activeTable.value?.tableId && !isDropMode ? (
              <div style="z-index: 9999;">
                <TableSheet
                  activeTable={activeTable}
                  isEditable={props.isEditable}
                  tables={props.tables}
                  onPayment={props.onPayment}
                  floorPlan={props.floorPlan}
                  onFloorPlanChange={props.onFloorPlanChange}
                />
              </div>
            ) : (
              <div></div>
            )}
          </Teleport>
        </div>
      );
    };
  },
});
